import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  ButtonGroup,
} from "react-bootstrap";
import Swal from "sweetalert2";
import {
  fetchConvenios,
  createConvenio,
  editConvenio,
  deactivateConvenio,
} from "../../redux/conveniosSlice";
import DocumentMentionInput from "../../components/DocumentMentionInput";

const ConveniosPage = () => {
  const dispatch = useDispatch();
  const { convenios, loading, error } = useSelector((state) => state.convenios);

  const [showModal, setShowModal] = useState(false);
  const [currentConvenio, setCurrentConvenio] = useState(null);
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    documento_referencia_id: null,
  });
  const [formErrors, setFormErrors] = useState({ titulo: "", descripcion: "" });
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    dispatch(fetchConvenios());
  }, [dispatch]);

  const validateField = (name, value) => {
    if (name === "titulo") {
      if (value.length < 4) {
        return "El título debe tener más de 3 caracteres.";
      }
    } else if (name === "descripcion") {
      if (value.length < 11) {
        return "La descripción debe tener más de 10 caracteres.";
      }
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  // Descripción vía DocumentMentionInput (recibe el texto directo).
  const handleDescripcionChange = (texto) => {
    setFormData((prev) => ({ ...prev, descripcion: texto }));
    setFormErrors((prev) => ({
      ...prev,
      descripcion: validateField("descripcion", texto),
    }));
  };

  // La BD guarda UN solo documento_referencia_id: la última mención elegida gana.
  const handleDocumentosChange = (docs) => {
    setFormData((prev) => ({
      ...prev,
      documento_referencia_id: docs.length ? docs[docs.length - 1].id : null,
    }));
  };

  const isFormValid = () => {
    return (
      formData.titulo.length >= 4 &&
      formData.descripcion.length >= 11 &&
      !formErrors.titulo &&
      !formErrors.descripcion
    );
  };

  const handleShowModal = (convenio = null) => {
    setCurrentConvenio(convenio);
    setFormData(
      convenio
        ? {
            titulo: convenio.titulo,
            descripcion: convenio.descripcion,
            documento_referencia_id: convenio.documento_referencia_id ?? null,
          }
        : { titulo: "", descripcion: "", documento_referencia_id: null },
    );
    setFormErrors({ titulo: "", descripcion: "" });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentConvenio(null);
    setFormData({ titulo: "", descripcion: "", documento_referencia_id: null });
    setFormErrors({ titulo: "", descripcion: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;

    setIsCreating(true);
    try {
      if (currentConvenio) {
        await dispatch(
          editConvenio({ id: currentConvenio.id, convenio: formData }),
        );
        Swal.fire("Éxito", "Convenio actualizado correctamente", "success");
      } else {
        await dispatch(createConvenio(formData));
        Swal.fire("Éxito", "Convenio creado correctamente", "success");
      }
      handleCloseModal();
    } catch (error) {
      Swal.fire("Error", "Hubo un problema al guardar el convenio", "error");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivate = async (id) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "No podrá revertir esta acción",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, eliminar",
    });

    if (result.isConfirmed) {
      try {
        await dispatch(deactivateConvenio(id));
        Swal.fire("Eliminado", "El convenio ha sido eliminado.", "success");
      } catch (error) {
        Swal.fire("Error", "Hubo un problema al eliminar el convenio", "error");
      }
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 className="h3 mb-0">Gestión de Convenios</h1>
        <Button
          onClick={() => handleShowModal()}
          variant="success"
          className="d-flex align-items-center"
        >
          <i className="bi bi-plus-circle me-2"></i>
          <span className="d-none d-sm-inline">Agregar Convenio</span>
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error.message ||
            (typeof error === "string" ? error : "Ocurrió un error")}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="d-block d-md-none alert alert-info mb-3">
            <i className="bi bi-phone me-1"></i>
            Desliza horizontalmente para ver todas las columnas
          </div>

          <div className="table-responsive">
            <Table striped bordered hover className="small">
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Título</th>
                  <th className="d-none d-md-table-cell">Descripción</th>
                  <th>Estado</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {convenios.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-4 text-muted">
                      No hay convenios registrados
                    </td>
                  </tr>
                ) : (
                  convenios.map((convenio) => (
                    <tr key={convenio.id}>
                      <td>{convenio.id}</td>
                      <td
                        title={convenio.titulo}
                        className="text-truncate"
                        style={{ maxWidth: "150px" }}
                      >
                        {convenio.titulo}
                      </td>
                      <td
                        title={convenio.descripcion}
                        className="d-none d-md-table-cell text-truncate"
                        style={{ maxWidth: "200px" }}
                      >
                        {convenio.descripcion}
                      </td>
                      <td>
                        <span
                          className={`badge ${convenio.activo ? "bg-success" : "bg-secondary"}`}
                        >
                          {convenio.activo ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="text-center">
                        <ButtonGroup size="sm">
                          <Button
                            variant="outline-primary"
                            onClick={() => handleShowModal(convenio)}
                            title="Editar"
                          >
                            <i className="bi bi-pencil"></i>
                            <span className="d-none d-lg-inline ms-1">
                              Editar
                            </span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => handleDeactivate(convenio.id)}
                            title="Eliminar"
                          >
                            <i className="bi bi-trash"></i>
                            <span className="d-none d-lg-inline ms-1">
                              Eliminar
                            </span>
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i
              className={`bi ${currentConvenio ? "bi-pencil-square" : "bi-plus-circle"} me-2`}
            ></i>
            {currentConvenio ? "Editar Convenio" : "Agregar Convenio"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3" controlId="formTitulo">
              <Form.Label>Título *</Form.Label>
              <Form.Control
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleChange}
                required
                placeholder="Ingrese el título del convenio"
                isInvalid={!!formErrors.titulo}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.titulo}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formDescripcion">
              <Form.Label>Descripción *</Form.Label>
              <Form.Text className="text-muted">
                Formato: <code>**negrita**</code>, <code>*cursiva*</code>,
                enlaces con <code>[texto](https://url.com)</code>. Los saltos de
                línea se respetan.
              </Form.Text>
              {/* Escribe @ para enlazar un documento existente (solo admin). */}
              <DocumentMentionInput
                value={formData.descripcion}
                onChange={handleDescripcionChange}
                onDocumentosChange={handleDocumentosChange}
                rows={4}
                placeholder="Descripción del convenio. Usa @ para enlazar un documento (opcional)."
              />
              {formErrors.descripcion && (
                <div className="text-danger small mt-1">
                  {formErrors.descripcion}
                </div>
              )}
              {formData.documento_referencia_id && (
                <div className="text-success small mt-1">
                  <i className="bi bi-link-45deg"></i> Documento enlazado (ID{" "}
                  {formData.documento_referencia_id})
                </div>
              )}
            </Form.Group>
            <Button
              variant={currentConvenio ? "warning" : "success"}
              type="submit"
              disabled={!isFormValid() || isCreating}
              className="w-100"
            >
              {isCreating ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{" "}
                  {currentConvenio ? "Actualizando..." : "Creando..."}
                </>
              ) : currentConvenio ? (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  Actualizar
                </>
              ) : (
                <>
                  <i className="bi bi-save me-1"></i>
                  Guardar
                </>
              )}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ConveniosPage;
